/**
 * Print queue (T07-25): every print is a row in Dexie `printJobs` first, then sent. A failed print
 * (cable pulled, paper out, bridge off) is retried automatically (3 tries in total), then stays
 * `failed` with a plain-language reason and the 「再印刷」 button — **a sale that succeeded is never
 * blocked or undone by the printer**. The bytes are the server's (T07-09); a retry re-sends the
 * same bytes (it is still the original, not a 「再発行」 copy).
 */
import type { PosDatabase, PrintJob } from "../db";
import { base64ToBytes } from "./escpos";
import { isPrinterError, type PrinterDriver, type PrinterErrorCode } from "./types";

export interface PrintQueueOptions {
  db: PosDatabase;
  driver: () => PrinterDriver;
  maxAttempts?: number;
  /** Wait before try 2, 3, … (ms). */
  retryDelaysMs?: readonly number[];
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
}

export interface NewPrintJob {
  label: string;
  data: string;
  saleId?: string | null;
  receiptNo?: string | null;
  openDrawer?: boolean;
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export class PrintQueue {
  private readonly db: PosDatabase;
  private readonly driver: () => PrinterDriver;
  private readonly maxAttempts: number;
  private readonly delays: readonly number[];
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly now: () => number;
  private chain: Promise<unknown> = Promise.resolve();

  constructor(options: PrintQueueOptions) {
    this.db = options.db;
    this.driver = options.driver;
    this.maxAttempts = options.maxAttempts ?? 3;
    this.delays = options.retryDelaysMs ?? [1_000, 3_000];
    this.sleep = options.sleep ?? defaultSleep;
    this.now = options.now ?? Date.now;
  }

  /** One job at a time (a printer takes one stream of bytes). */
  private serial<T>(task: () => Promise<T>): Promise<T> {
    const run = this.chain.then(task, task);
    this.chain = run.catch(() => undefined);
    return run;
  }

  /** Queue and print; resolves with the job as it ended (`done` / `failed`) — never throws. */
  async submit(input: NewPrintJob): Promise<PrintJob> {
    const at = this.now();
    const job: PrintJob = {
      label: input.label,
      saleId: input.saleId ?? null,
      receiptNo: input.receiptNo ?? null,
      data: input.data,
      openDrawer: input.openDrawer ?? false,
      status: "queued",
      attempts: 0,
      lastError: null,
      createdAt: at,
      updatedAt: at,
    };
    const id = (await this.db.printJobs.add(job)) as number;
    return this.serial(() => this.run(id));
  }

  /** 「再印刷」 of a failed job: three more tries with the same bytes. */
  async retry(id: number): Promise<PrintJob> {
    await this.db.printJobs.update(id, {
      status: "queued",
      attempts: 0,
      lastError: null,
      updatedAt: this.now(),
    });
    return this.serial(() => this.run(id));
  }

  async dismiss(id: number): Promise<void> {
    await this.db.printJobs.delete(id);
  }

  /** Kick the cash drawer on its own (cash sale) — `false` when the printer is not reachable. */
  async openDrawer(): Promise<{ ok: boolean; error?: PrinterErrorCode }> {
    return this.serial(async () => {
      try {
        await this.driver().openDrawer();
        return { ok: true };
      } catch (error) {
        return { ok: false, error: isPrinterError(error) ? error.code : "write_failed" };
      }
    });
  }

  async failed(): Promise<PrintJob[]> {
    return this.db.printJobs.where("status").equals("failed").sortBy("createdAt");
  }

  private async run(id: number): Promise<PrintJob> {
    const stored = await this.db.printJobs.get(id);
    if (!stored) {
      throw new Error(`print job ${String(id)} is gone`);
    }
    let job = stored;
    const bytes = base64ToBytes(job.data);
    const start = stored.attempts;
    for (let attempt = start; attempt < this.maxAttempts; attempt += 1) {
      if (attempt > start) {
        const wait = this.delays[Math.min(attempt - start - 1, this.delays.length - 1)];
        await this.sleep(wait ?? 1_000);
      }
      job = { ...job, status: "printing", attempts: attempt + 1, updatedAt: this.now() };
      await this.db.printJobs.put(job);
      try {
        await this.driver().print(bytes);
        job = { ...job, status: "done", lastError: null, updatedAt: this.now() };
        await this.db.printJobs.put(job);
        await this.prune();
        return job;
      } catch (error) {
        job = {
          ...job,
          status: "failed",
          lastError: isPrinterError(error) ? error.code : "write_failed",
          updatedAt: this.now(),
        };
        await this.db.printJobs.put(job);
      }
    }
    return job;
  }

  /** Keep the last 50 finished jobs (history), failed ones stay until printed / dismissed. */
  private async prune(): Promise<void> {
    const done = await this.db.printJobs.where("status").equals("done").sortBy("createdAt");
    const extra = done.length - 50;
    if (extra > 0) {
      await this.db.printJobs.bulkDelete(done.slice(0, extra).map((j) => j.id as number));
    }
  }
}
